import { ColDef, GridApi, GridOptions } from '@ag-grid-community/core'
import { Component, EventEmitter, Input, Output } from '@angular/core'
import { ValidatorFn } from '@angular/forms'
import {
  AngularColumnTypeEnum,
  InlineEditDataType,
  NgGridComponents,
  getInlineEditColumnType,
} from '@northfork/ag-grid-angular'
import { FilterGridRow, UpdatedFilter } from '../../models/filter-grid.model'
import {
  BuiltinFilterOperator,
  FilterOperator,
} from '../../models/filter-operator.model'

@Component({
  selector: 'hs-filter-grid',
  templateUrl: './filter-grid.component.html',
  styleUrls: ['./filter-grid.component.scss'],
})
export class FilterGridComponent {
  /** @internal */
  gridApi?: GridApi

  private _data: FilterGridRow[] = []
  @Input() set data(value: FilterGridRow[]) {
    this._data = value
    this.setFocus()
  }

  get data(): FilterGridRow[] {
    return this._data
  }

  @Input() validators: { [colId: string]: ValidatorFn } = {}

  @Output() change = new EventEmitter<UpdatedFilter>()
  @Output() complete = new EventEmitter<boolean>()
  @Output() close = new EventEmitter<boolean>()

  readonly gridOptions: GridOptions = this.createGridOptions()

  private createColumnDefs(): ColDef[] {
    return [
      {
        field: 'column',
        width: 300,
      },
      {
        field: 'operator',
        editable: true,
        width: 150,
        type: getInlineEditColumnType(),
        cellEditorSelector: params => {
          const row = params.data as FilterGridRow
          return {
            component: NgGridComponents.inlineEditor,
            params: {
              dataType: InlineEditDataType.Select,
              options: {
                items: row.availableOperators,
                bindLabel: 'name',
                trackByFn: (item: FilterOperator) => item.type,
              },
            },
            popup: true,
          }
        },
        onCellValueChanged: params => {
          const row = params.data as FilterGridRow
          row.operator = params.newValue

          this.change.next({
            column: row.column,
            filterValue: row.filterValue,
            operator: row.operator,
            field: row.field,
          })
        },
        valueFormatter: params => {
          const defaultOperator = (params.data as FilterGridRow)
            .availableOperators[0]
          return params.value?.name ?? defaultOperator.name
        },
      },
      {
        field: 'filterValue',
        editable: true,
        width: 300,
        type: getInlineEditColumnType(),
        initialSort: 'desc',
        cellEditorSelector: params => {
          const row = params.data as FilterGridRow
          if (row.editType === InlineEditDataType.Select) {
            return {
              component: NgGridComponents.inlineEditor,
              type: getInlineEditColumnType(AngularColumnTypeEnum.InlineEdit),
              params: {
                dataType: InlineEditDataType.MultiSelect,
                options: {
                  items: [...(row.values ?? [])],
                },
              },
            }
          }

          let dataType = row.editType

          if (
            row.operator === BuiltinFilterOperator.in ||
            row.operator === BuiltinFilterOperator.notIn
          ) {
            dataType = InlineEditDataType.String
          }
          const validators: ValidatorFn | undefined =
            row.field === undefined || this.validators[row.field] === undefined
              ? undefined
              : this.validators[row.field]

          return {
            component: NgGridComponents.inlineEditor,
            type: getInlineEditColumnType(AngularColumnTypeEnum.InlineEdit),
            params: {
              dataType,
              validators,
            },
          }
        },
        valueGetter: params => {
          const row = params.data as FilterGridRow
          return row.filterValue
        },
        onCellValueChanged: params => {
          const row = params.data as FilterGridRow
          row.filterValue = params.newValue

          if (row.filterValue === 'True') {
            row.filterValue = [true]
          } else if (row.filterValue === 'False') {
            row.filterValue = [false]
          }

          this.change.next({
            column: row.column,
            filterValue: row.filterValue,
            operator: row.operator,
            field: row.field,
          })
        },
      },
    ]
  }

  private createGridOptions(): GridOptions {
    return {
      groupDisplayType: 'groupRows',
      defaultColDef: {
        resizable: true,
        sortable: true,
      },
      columnDefs: this.createColumnDefs(),
      autoGroupColumnDef: {},
      sideBar: false,
      animateRows: true,
      rowHeight: 27,
      getContextMenuItems: params => {
        return [
          {
            name: 'Clear Row',
            action: () => {
              params.node?.setDataValue('filterValue', '')
            },
          },
        ]
      },
      onGridReady: params => {
        this.gridApi = params.api
        this.setFocus()
      },
      onCellKeyDown: params => {
        const event = params.event as KeyboardEvent
        const inputElement = event.target as HTMLElement

        if (inputElement.tagName === 'INPUT') {
          return
        }

        if (event.key === 'Delete' || event.key === 'Backspace') {
          const data = (params.data as FilterGridRow) ?? {}
          data.filterValue = ''
          params.api.applyTransaction({ update: [data] })
          this.change.next(data)
        }
      },
      onCellKeyPress: params => {
        const event = params.event as KeyboardEvent

        const inputElement = event.target as HTMLElement

        if (inputElement.tagName === 'INPUT') {
          return
        }

        if (event.key === 'Enter' && event.altKey) {
          event.preventDefault()
          event.stopPropagation()
          this.complete.emit(true)
        }
      },
    }
  }

  /** @internal */
  setFocus() {
    // Some race condition with the grid rendering and the bootstrap model changes focus
    setTimeout(() => {
      this.gridApi?.setFocusedCell(0, 'filterValue')
    }, 100)
  }
}
